// src/components/Search.js
import React, { useState, useEffect } from "react";
import { Drawer, Box, TextField, Avatar } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import { db } from "../../firebase-config";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";

const Search = ({ open, onClose, onSelectUser }) => {
  const theme = useTheme();
  const backgroundImage =
    theme.palette.mode === "dark"
      ? "url('/assets/dogwood-texture-dark.png')"
      : "url('/assets/dogwood-texture.png')";

  const { user } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Only search if a user is logged in and the input has at least 2 characters.
    if (!user || searchText.trim().length < 2) {
      setSearchOptions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const usersRef = collection(db, "users");
        // Trim the input and convert it to lowercase.
        const trimmedText = searchText.trim();
        const normalizedText = trimmedText.toLowerCase();
        let results = [];

        // If there is a space in the input, assume it's a firstName/lastName query.
        if (normalizedText.includes(" ")) {
          const parts = normalizedText.split(/\s+/);
          if (parts.length >= 2) {
            const firstPart = parts[0];
            const secondPart = parts[1];
            // Query separately for firstNameLower and lastNameLower.
            const firstNameQuery = query(
              usersRef,
              where("firstNameLower", ">=", firstPart),
              where("firstNameLower", "<=", firstPart + "\uf8ff")
            );
            const lastNameQuery = query(
              usersRef,
              where("lastNameLower", ">=", secondPart),
              where("lastNameLower", "<=", secondPart + "\uf8ff")
            );
            const [firstNameSnap, lastNameSnap] = await Promise.all([
              getDocs(firstNameQuery),
              getDocs(lastNameQuery),
            ]);
            const firstResults = new Map();
            firstNameSnap.forEach((docSnapshot) => {
              if (docSnapshot.id !== user.uid) {
                firstResults.set(docSnapshot.id, {
                  id: docSnapshot.id,
                  ...docSnapshot.data(),
                });
              }
            });
            const lastResults = new Map();
            lastNameSnap.forEach((docSnapshot) => {
              if (docSnapshot.id !== user.uid) {
                lastResults.set(docSnapshot.id, {
                  id: docSnapshot.id,
                  ...docSnapshot.data(),
                });
              }
            });
            // Intersect the two result sets (match users that appear in both queries).
            firstResults.forEach((data, id) => {
              if (lastResults.has(id)) {
                results.push({
                  id,
                  firstName: data.firstName || "",
                  lastName: data.lastName || "",
                  bagTag: data.bagTag || "",
                  photoURL: data.photoURL || "",
                });
              }
            });
          }
        } else {
          // If there's no space, perform the original logic on bagTag, firstNameLower, and lastNameLower.
          const bagTagQuery = query(
            usersRef,
            where("bagTag", ">=", normalizedText),
            where("bagTag", "<=", normalizedText + "\uf8ff")
          );
          const firstNameQuery = query(
            usersRef,
            where("firstNameLower", ">=", normalizedText),
            where("firstNameLower", "<=", normalizedText + "\uf8ff")
          );
          const lastNameQuery = query(
            usersRef,
            where("lastNameLower", ">=", normalizedText),
            where("lastNameLower", "<=", normalizedText + "\uf8ff")
          );

          const [bagTagSnap, firstNameSnap, lastNameSnap] = await Promise.all([
            getDocs(bagTagQuery),
            getDocs(firstNameQuery),
            getDocs(lastNameQuery),
          ]);

          const resultsMap = new Map();
          const processSnapshot = (snapshot) => {
            snapshot.forEach((docSnapshot) => {
              if (docSnapshot.id !== user.uid) {
                const data = docSnapshot.data();
                resultsMap.set(docSnapshot.id, {
                  id: docSnapshot.id,
                  firstName: data.firstName || "",
                  lastName: data.lastName || "",
                  bagTag: data.bagTag || "",
                  photoURL: data.photoURL || "",
                });
              }
            });
          };

          processSnapshot(bagTagSnap);
          processSnapshot(firstNameSnap);
          processSnapshot(lastNameSnap);

          results = Array.from(resultsMap.values());
        }

        setSearchOptions(results);
        setIsLoading(false);
      } catch (error) {
        console.error("Error during search", error);
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, user]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "#AFE1AF",
          backgroundImage,
          backgroundRepeat: "repeat",
          backgroundSize: "200px",
        },
      }}
    >
      <Box sx={{ width: 300, p: 2 }}>
        <Autocomplete
          // Open only when input has at least 2 characters.
          open={searchText.trim().length >= 2}
          options={searchOptions}
          loading={isLoading}
          loadingText="Searchin'..."
          noOptionsText="No players found"
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName}` +
            (option.bagTag ? ` (${option.bagTag})` : "")
          }
          renderOption={(props, option) => {
            const { key, ...restProps } = props;
            return (
              <Box key={key} {...restProps} sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  src={option.photoURL || undefined}
                  alt={`${option.firstName} ${option.lastName}`}
                  sx={{ width: 40, height: 40, mr: 1 }}
                />
                <span>
                  {option.firstName} {option.lastName}
                  {option.bagTag ? ` (${option.bagTag})` : ""}
                </span>
              </Box>
            );
          }}
          inputValue={searchText}
          onInputChange={(event, newValue) => setSearchText(newValue)}
          onChange={(event, value) => {
            if (value) {
              onSelectUser(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder="Search Bag Tags / Names"
            />
          )}
        />
      </Box>
    </Drawer>
  );
};

export default Search;